// Copyright (c) Docugami, Inc. All rights reserved.

import { MutableRefObject } from "react";
import { WorldSpaceConfiguration, transformPointDocumentToWorldSpace } from "sdv/Space";
import { Point } from "sdv/Types";
import { IInputSource, InputController } from "./InputController";

type EventMap = {
    [Key in keyof HTMLElementEventMap]?: (this: HTMLElement, ev: HTMLElementEventMap[Key]) => any
};

/**
 * Implements the IInputSource utilized by the SDV engine driven by a given
 * HTML element source.
 */
export class HtmlElementInputSource implements IInputSource {
    private _inputController: InputController | null = null;
    private _viewElement: MutableRefObject<HTMLElement | undefined>;
    private _scrollElement: MutableRefObject<HTMLElement | undefined>;
    private readonly _documentEventMap: EventMap;
    private readonly _viewEventMap: EventMap;
    private readonly _globalEventMap: EventMap;
    private readonly _scrollEventMap: EventMap;
    private readonly _spaceConfig: WorldSpaceConfiguration;

    private _lastPointerPosition: Point | null = null;

    private _initialized: boolean = false;
    private _globalCapture: boolean = false;

    constructor(viewElement: MutableRefObject<HTMLElement | undefined>, scrollElement: MutableRefObject<HTMLElement | undefined>, spaceConfig: WorldSpaceConfiguration) {
        this._viewElement = viewElement;
        this._scrollElement = scrollElement;
        this._spaceConfig = spaceConfig;

        this._viewEventMap = {
            "pointerleave": this.onPointerLeave.bind(this),
            "pointermove": this.onPointerMove.bind(this),
            "pointerdown": this.onPointerDown.bind(this),
            "pointerup": this.onPointerUp.bind(this),
        }

        this._documentEventMap = {
            "keydown": this.onKeyDown.bind(this),
            "keyup": this.onKeyUp.bind(this),
            "blur": this.onPointerLeave.bind(this)
        }

        this._globalEventMap = {
            "keydown": this.onKeyDown.bind(this),
            "keyup": this.onKeyUp.bind(this),
            "pointermove": this.onPointerMove.bind(this),
            "pointerup": this.onPointerUp.bind(this),
            "blur": this.onPointerLeave.bind(this)
        }

        this._scrollEventMap = {
            "scroll": this.onScroll.bind(this)
        };
    }

    start(inputController: InputController) {
        this._inputController = inputController;
        this.ensureInitialized();
    }

    stop() {
        this.uninitialize();
        this._inputController = null;
    }

    update() {
        this.ensureInitialized();
    }

    cancelGlobalCapture() {
        this.stopGlobalEventCapture();
    }

    private onPointerLeave(): void {
        this.stopGlobalEventCapture();
        this._inputController!.onPointerLeave();
        this._lastPointerPosition = null;
    }

    private onPointerMove(ev: PointerEvent): void {
        if (!this._viewElement.current || !this._scrollElement.current) {
            this.uninitialize();
            return;
        }

        this._lastPointerPosition = {
            x: ev.clientX,
            y: ev.clientY
        }

        const scale = this._spaceConfig.width / this._viewElement.current.offsetWidth;

        const worldPoint = transformPointDocumentToWorldSpace({ x: ev.clientX, y: ev.clientY }, this._scrollElement.current, scale, !this._globalCapture);
        this._inputController!.onPointerMove(worldPoint);
    }

    private onPointerDown(ev: PointerEvent): void {
        if (ev.shiftKey) {
            return;
        }

        this.startGlobalEventCapture();
        this._inputController!.onPointerDown(ev.button);
    }

    private onPointerUp(ev: PointerEvent): void {
        if (ev.shiftKey) {
            return;
        }

        this.stopGlobalEventCapture();
        this._inputController!.onPointerUp(ev.button);
    }

    private onKeyDown(ev: KeyboardEvent): void {
        this._inputController!.onKeyDown(ev.key);
    }

    private onKeyUp(ev: KeyboardEvent): void {
        this._inputController!.onKeyUp(ev.key);
    }

    private onScroll() {
        if (!this._viewElement.current || !this._scrollElement.current) {
            this.uninitialize();
            return;
        }

        if (!this._lastPointerPosition) {
            return;
        }

        const scale = this._spaceConfig.width / this._viewElement.current.offsetWidth;

        const worldPoint = transformPointDocumentToWorldSpace(this._lastPointerPosition, this._scrollElement.current, scale, !this._globalCapture);
        this._inputController!.onPointerMove(worldPoint);
    }

    private ensureInitialized() {
        if (this._initialized) {
            return;
        }

        if (!this._viewElement.current) {
            return;
        }

        if (!this._scrollElement.current) {
            return;
        }

        if (!this._inputController) {
            return;
        }

        this._initialized = true;

        registerEventMap(document, this._documentEventMap);
        registerEventMap(this._viewElement.current, this._viewEventMap);
        registerEventMap(this._scrollElement.current, this._scrollEventMap);
    }

    private uninitialize() {
        if (!this._initialized) {
            return;
        }

        this._initialized = false;
        this._globalCapture = false;

        unregisterEventMap(document, this._documentEventMap);
        unregisterEventMap(document, this._globalEventMap);

        if (!!this._viewElement.current) {
            unregisterEventMap(this._viewElement.current, this._viewEventMap);
        }

        if (!!this._scrollElement.current) {
            unregisterEventMap(this._scrollElement.current, this._scrollEventMap);
        }
    }

    private startGlobalEventCapture() {
        if (this._globalCapture) {
            return;
        }

        this._globalCapture = true;

        unregisterEventMap(document, this._documentEventMap);
        unregisterEventMap(this._viewElement.current!, this._viewEventMap);

        registerEventMap(document, this._globalEventMap);
    }

    private stopGlobalEventCapture() {
        if (!this._globalCapture) {
            return;
        }

        this._globalCapture = false;

        unregisterEventMap(document, this._globalEventMap);

        registerEventMap(document, this._documentEventMap);
        registerEventMap(this._viewElement.current!, this._viewEventMap);
    }

}

function registerEventMap(target: HTMLElement | Document | Window, map: EventMap) {
    for (const [k, func] of Object.entries(map)) {
        const key = k as keyof HTMLElementEventMap;
        target.addEventListener(key as keyof HTMLElementEventMap, func as (this: HTMLElement, ev: HTMLElementEventMap[typeof key]) => any);
    }
}

function unregisterEventMap(target: HTMLElement | Document | Window, map: EventMap) {
    for (const [k, func] of Object.entries(map)) {
        const key = k as keyof HTMLElementEventMap;
        target.removeEventListener(key as keyof HTMLElementEventMap, func as (this: HTMLElement, ev: HTMLElementEventMap[typeof key]) => any);
    }
}
